import { Box, Collapse, Divider, useMediaQuery, useTheme } from '@mui/material';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDownIcon2x } from '../Icons/ChevronDownIcon';
import {
  FooterButton,
  FooterContainer,
  FooterHeader,
  FooterText,
  FooterLink,
  LinksContainer,
  FooterButtonText,
  ButtonTextContainer,
  GridContainer,
  GridItem,
  FooterDivider,
} from './FooterStyle';
import ClickableLink from '../Text/ClickableLink';
import VodafoneIcon from '../Icons/VodafoneIcon';

function Footer() {
  const [t] = useTranslation();
  const theme = useTheme();

  const isMdOrLower = useMediaQuery(theme.breakpoints.down('lg'));
  const [showMoreProducts, setShowMoreProducts] = useState(!isMdOrLower);
  const [showMoreSolutions, setShowMoreSolutions] = useState(!isMdOrLower);
  const [showMoreAbout, setShowMoreAbout] = useState(!isMdOrLower);

  useEffect(() => {
    setShowMoreProducts(!isMdOrLower);
    setShowMoreSolutions(!isMdOrLower);
    setShowMoreAbout(!isMdOrLower);
  }, [isMdOrLower]);

  return (
    <FooterContainer>
      <Box sx={{ margin: 0, width: '100%', maxWidth: 'calc(0% + 92rem)' }}>
        <Box sx={{ width: '2.25rem', height: '2.25rem' }}>
          <ClickableLink to="https://www.vodafone.com/business" target="_blank">
            <VodafoneIcon />
          </ClickableLink>
        </Box>
        <Divider
          sx={{
            borderColor: '#808080',
            marginTop: '2rem',
            marginBottom: isMdOrLower ? '1.25rem' : '0.625rem',
          }}
        />
        <GridContainer container spacing={3}>
          <GridItem item xs={12} md={12} lg={4} xl={4}>
            <Box>
              {isMdOrLower && (
                <FooterButton
                  color="secondaryButton"
                  label={
                    <>
                      <ButtonTextContainer>
                        <FooterButtonText>{t('footer.products')}</FooterButtonText>
                      </ButtonTextContainer>
                      <Box
                        sx={{
                          paddingLeft: '0.5rem',
                          '& svg': { transform: showMoreProducts ? 'rotate(180deg)' : 'rotate(0deg)' },
                        }}
                      >
                        <ChevronDownIcon2x stroke={theme.palette.monochrome.monoChrome13} />
                      </Box>
                    </>
                  }
                  onClick={() => setShowMoreProducts((prev) => !prev)}
                />
              )}
              <Collapse sx={{ marginTop: showMoreProducts ? '1rem' : 0 }} in={showMoreProducts}>
                <LinksContainer>
                  {!isMdOrLower && (
                    <FooterHeader sx={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>
                      {t('footer.products')}
                    </FooterHeader>
                  )}
                  <FooterLink target="_blank" to="https://www.vodafone.com/business/products/unified-communications">
                    {t('footer.unifiedCommunications')}
                  </FooterLink>
                  <FooterLink target="_blank" to="https://www.vodafone.com/business/products/mobile-connectivity">
                    {t('footer.mobileConnectivity')}
                  </FooterLink>
                  <FooterLink target="_blank" to="https://www.vodafone.com/business/products/fixed-connectivity">
                    {t('footer.fixedConnectivity')}
                  </FooterLink>
                  <FooterLink target="_blank" to="https://www.vodafone.com/business/products/digital-applications">
                    {t('footer.digitalApplications')}
                  </FooterLink>
                  <FooterLink target="_blank" to="https://www.vodafone.com/business/products/iot-connectivity">
                    {t('footer.iotConnectivity')}
                  </FooterLink>
                  <FooterLink target="_blank" to="https://www.vodafone.com/business/products/cloud-and-edge">
                    {t('footer.cloudAndEdge')}
                  </FooterLink>
                  <FooterLink target="_blank" to="https://www.vodafone.com/business/products/digital-solutions">
                    {t('footer.e2eSolutions')}
                  </FooterLink>
                  <FooterLink target="_blank" to="https://www.vodafone.com/business/products/cybersecurity">
                    {t('footer.security')}
                  </FooterLink>
                </LinksContainer>
              </Collapse>
              {isMdOrLower && <Divider sx={{ borderColor: '#808080', margin: '0.5rem 0' }} />}
            </Box>
          </GridItem>

          <GridItem item xs={12} md={12} lg={4} xl={4}>
            <Box>
              {isMdOrLower && (
                <FooterButton
                  color="secondaryButton"
                  label={
                    <>
                      <ButtonTextContainer>
                        <FooterButtonText>{t('footer.solutionsFor')}</FooterButtonText>
                      </ButtonTextContainer>
                      <Box
                        sx={{
                          paddingLeft: '0.5rem',
                          '& svg': { transform: showMoreSolutions ? 'rotate(180deg)' : 'rotate(0deg)' },
                        }}
                      >
                        <ChevronDownIcon2x stroke={theme.palette.monochrome.monoChrome13} />
                      </Box>
                    </>
                  }
                  onClick={() => setShowMoreSolutions((prev) => !prev)}
                />
              )}
              <Collapse sx={{ marginTop: showMoreSolutions ? '1rem' : 0 }} in={showMoreSolutions}>
                <LinksContainer>
                  {!isMdOrLower && (
                    <FooterHeader sx={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>
                      {t('footer.solutionsFor')}
                    </FooterHeader>
                  )}
                  <FooterLink
                    target="_blank"
                    to="https://www.vodafone.com/business/solutions/by-business-type/large-multinational-business"
                  >
                    {t('footer.largeMultinationalBusinesses')}
                  </FooterLink>
                  <FooterLink
                    target="_blank"
                    to="https://www.vodafone.com/business/solutions/by-business-type/small-medium-business"
                  >
                    {t('footer.smallMediumBusinesses')}
                  </FooterLink>
                  <FooterLink
                    target="_blank"
                    to="https://www.vodafone.com/business/solutions/by-business-type/public-sector"
                  >
                    {t('footer.internationalPublicSector')}
                  </FooterLink>
                  <FooterLink
                    target="_blank"
                    to="https://www.vodafone.com/business/solutions/by-business-type/vodafone-carrier-services"
                  >
                    {t('footer.carriersDigitalServiceProviders')}
                  </FooterLink>
                </LinksContainer>
              </Collapse>
              {isMdOrLower && <Divider sx={{ borderColor: '#808080', margin: '0.5rem 0' }} />}
            </Box>
          </GridItem>

          <GridItem item xs={12} md={12} lg={4} xl={4}>
            <Box>
              {isMdOrLower && (
                <FooterButton
                  color="secondaryButton"
                  label={
                    <>
                      <ButtonTextContainer>
                        <FooterButtonText>{t('footer.about')}</FooterButtonText>
                      </ButtonTextContainer>
                      <Box
                        sx={{
                          paddingLeft: '0.5rem',
                          '& svg': { transform: showMoreAbout ? 'rotate(180deg)' : 'rotate(0deg)' },
                        }}
                      >
                        <ChevronDownIcon2x stroke={theme.palette.monochrome.monoChrome13} />
                      </Box>
                    </>
                  }
                  onClick={() => setShowMoreAbout((prev) => !prev)}
                />
              )}
              <Collapse sx={{ marginTop: showMoreAbout ? '1rem' : 0 }} in={showMoreAbout}>
                <LinksContainer>
                  {!isMdOrLower && (
                    <FooterHeader sx={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>{t('footer.about')}</FooterHeader>
                  )}
                  <FooterLink target="_blank" to="https://careers.vodafone.com/">
                    {t('footer.careers')}
                  </FooterLink>
                  <FooterLink target="_blank" to="https://www.vodafone.com/business/about/sustainability">
                    {t('footer.sustainability')}
                  </FooterLink>
                </LinksContainer>
              </Collapse>
            </Box>
          </GridItem>
        </GridContainer>

        <Box
          sx={{
            marginBottom: '1.75rem',
            paddingTop: '2.5rem',
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            justifyContent: 'center',
          }}
        >
          <FooterLink target="_blank" to="https://www.vodafone.com/business/customer-terms/code-of-conduct">
            {t('footer.codeOfConduct')}
          </FooterLink>
          <FooterDivider orientation="vertical" flexItem />
          <FooterLink target="_blank" to="https://business.vodafone.com/SubscriptionCentre?src=groupWebsite">
            {t('footer.preferenceCentre')}
          </FooterLink>
          <FooterDivider orientation="vertical" flexItem />
          <FooterLink target="_blank" to={import.meta.env.VITE_PRIVACY_POLICY_URL}>
            {t('footer.privacyPolicy')}
          </FooterLink>
          <FooterDivider orientation="vertical" flexItem />
          <FooterLink target="_blank" to="/cookiepolicy">
            {t('footer.cookiePolicy')}
          </FooterLink>
          <FooterDivider orientation="vertical" flexItem />
          <FooterLink target="_blank" to="https://www.vodafone.com/business/customer-terms/digital-services-act">
            {t('footer.digitalServicesAct')}
          </FooterLink>
          <FooterDivider orientation="vertical" flexItem />
          <FooterLink target="_blank" to="/termsandconditions">
            {t('footer.termsAndConditions')}
          </FooterLink>
        </Box>

        {/* Footer bottom text */}
        <Box sx={{ marginBottom: '1.75rem', textAlign: 'center' }}>
          <FooterText>{t('footer.copyright', { year: new Date().getFullYear() })}</FooterText>
        </Box>
      </Box>
    </FooterContainer>
  );
}

export default Footer;
